import { useState } from "react";

import { Field } from "@/components/field";
import { NativeSelect } from "@/components/native-select";
import { Input } from "@/components/ui/input";

// Sentinel select value that switches the control into free-text mode.
const OTHER = "__other__";

interface SelectOrOtherProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
}

/**
 * Select with an "Other…" escape hatch that swaps to a free-text input. The
 * stored value is always the plain string, whichever mode produced it.
 */
export function SelectOrOther({
  label,
  hint,
  value,
  onChange,
  options,
  placeholder = "Select…",
}: SelectOrOtherProps) {
  const [otherMode, setOtherMode] = useState(() => value !== "" && !options.includes(value));

  if (otherMode) {
    return (
      <Field label={label} hint={hint}>
        <Input
          type="text"
          placeholder="Type your own…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="text-accent text-xs font-medium hover:underline"
          onClick={() => {
            setOtherMode(false);
            onChange("");
          }}
        >
          ← Choose from list
        </button>
      </Field>
    );
  }

  return (
    <Field label={label} hint={hint}>
      <NativeSelect
        value={value}
        onChange={(e) => {
          if (e.target.value === OTHER) {
            setOtherMode(true);
            onChange("");
          } else {
            onChange(e.target.value);
          }
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
        <option value={OTHER}>Other…</option>
      </NativeSelect>
    </Field>
  );
}
